import React from 'react';
import { TickerDisplayData } from '../types/ticker';
import { UpArrowIcon, DownArrowIcon } from '../components/Icons';
import {
  FUNDAMENTAL_STATISTICS,
  ANNUAL_FUNDAMENTAL_STATISTICS,
} from '../utils/fundamentalsTabUtils';

export interface UseFundamentalsTabStatisticsOptions {
  timeframe: 'quarterly' | 'annual';
  // Optional syncing state handling (for ListFundamentals)
  syncingTickers?: Set<string>;
  unsyncedTickers?: Set<string>;
  pollingLimitReached?: Set<string>;
}

/**
 * Get arrow direction and color based on comparison logic
 */
export const getFundamentalsArrowInfo = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseFundamentalsTabStatisticsOptions
): { direction: 'up' | 'down' | null, isGood: boolean } => {
  const { timeframe } = options;
  const comparisonData = (ticker as any)._comparisonData;
  if (!comparisonData || !comparisonData[statisticKey]) {
    return { direction: null, isGood: false };
  }

  const data = comparisonData[statisticKey];
  const latest = data.latest;
  const previous = data.previous;
  const sameQuarterLastYear = data.sameQuarterLastYear;

  if (latest === undefined || latest === null) {
    return { direction: null, isGood: false };
  }

  if (timeframe === 'annual') {
    // Annual timeframe: All comparisons are latest year vs previous year
    if (previous === undefined || previous === null) return { direction: null, isGood: false };
    
    // End of Fiscal Year Valuation section: Compare latest year to previous year
    if (['pe_ratio', 'ps_ratio', 'pb_ratio', 'ev_fcf_ratio', 'p_fcf_ratio', 'dividend_yield'].includes(statisticKey)) {
      // For P/E, P/S, P/B ratios, lower is better
      if (['pe_ratio', 'ps_ratio', 'pb_ratio', 'ev_fcf_ratio', 'p_fcf_ratio'].includes(statisticKey)) {
        if (latest < previous) return { direction: 'down', isGood: true };
        if (latest > previous) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      
      // For dividend yield, higher is better
      if (statisticKey === 'dividend_yield') {
        if (latest > previous) return { direction: 'up', isGood: true };
        if (latest < previous) return { direction: 'down', isGood: false };
        return { direction: null, isGood: false };
      }
    }

    // Growth section: Compare latest year to previous year
    if (['yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth'].includes(statisticKey)) {
      // For growth metrics, higher is better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Profitability section: Compare latest year to previous year
    if (['gross_margin', 'operating_margin', 'net_margin', 'roa', 'roe', 'roic'].includes(statisticKey)) {
      // For profitability metrics, higher is better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Profitability financials: Compare latest year to previous year
    if (['revenues', 'gross_profit', 'operating_income_loss', 'net_income_loss', 'diluted_earnings_per_share_adjusted'].includes(statisticKey)) {
      // For financial metrics, higher is better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Profitability expenses: Compare latest year to previous year
    if (['operating_expense', 'sg_and_a', 'r_and_d'].includes(statisticKey)) {
      // For expense metrics, lower is better
      if (latest < previous) return { direction: 'down', isGood: true };
      if (latest > previous) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }

    // Balance Sheet section: Compare latest year to previous year
    if (['current_ratio', 'quick_ratio', 'book_value_per_share'].includes(statisticKey)) {
      // For balance sheet ratios, higher is generally better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Balance Sheet financials: Compare latest year to previous year
    if (['assets', 'equity', 'current_assets', 'current_liabilities', 'inventory', 'total_debt'].includes(statisticKey)) {
      // For balance sheet financials, higher is generally better (except liabilities and debt)
      if (statisticKey === 'current_liabilities' || statisticKey === 'total_debt') {
        if (latest < previous) return { direction: 'down', isGood: true };
        if (latest > previous) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Cash Flow section: Compare latest year to previous year
    if (['operating_cash_flow', 'investing_cash_flow', 'financing_cash_flow', 'end_cash_position', 'capital_expenditure', 'free_cash_flow', 'cash_flow_margin', 'ocf_ratio', 'fcf_yield'].includes(statisticKey)) {
      // For cash flow metrics, higher is generally better (except capital expenditure and investing cash flow)
      if (statisticKey === 'capital_expenditure' || statisticKey === 'investing_cash_flow') {
        // For capital expenditure and investing cash flow, lower is better (less spending)
        if (latest < previous) return { direction: 'down', isGood: true };
        if (latest > previous) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }
  } else {
    // Quarterly timeframe: Original logic
    // Latest Valuation section: Compare current quarter to latest ended quarter
    if (['pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio', 'ev_fcf_ratio', 'p_fcf_ratio', 'dividend_yield'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For P/E, P/S, P/B ratios, lower is better
      if (['pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio', 'ev_fcf_ratio', 'p_fcf_ratio'].includes(statisticKey)) {
        if (latest < previous) return { direction: 'down', isGood: true };
        if (latest > previous) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      
      // For dividend yield, higher is better
      if (statisticKey === 'dividend_yield') {
        if (latest > previous) return { direction: 'up', isGood: true };
        if (latest < previous) return { direction: 'down', isGood: false };
        return { direction: null, isGood: false };
      }
    }

    // Growth section: Compare latest ended quarter to same quarter last year
    if (['qoq_revenue_growth', 'qoq_net_income_growth', 'qoq_eps_growth', 'yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth'].includes(statisticKey)) {
      if (sameQuarterLastYear === undefined || sameQuarterLastYear === null) return { direction: null, isGood: false };
      
      // For growth metrics, higher is better
      if (latest > sameQuarterLastYear) return { direction: 'up', isGood: true };
      if (latest < sameQuarterLastYear) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Profitability section: Compare latest ended quarter to previous quarter
    if (['gross_margin', 'operating_margin', 'net_margin', 'roa', 'roe', 'roic'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For profitability metrics, higher is better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Profitability financials: Compare latest ended quarter to previous quarter
    if (['revenues', 'gross_profit', 'operating_income_loss', 'net_income_loss', 'diluted_earnings_per_share_adjusted'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For financial metrics, higher is better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Profitability expenses: Compare latest ended quarter to previous quarter
    if (['operating_expense', 'sg_and_a', 'r_and_d'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For expense metrics, lower is better
      if (latest < previous) return { direction: 'down', isGood: true };
      if (latest > previous) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }

    // Balance Sheet section: Compare latest ended quarter to previous quarter
    if (['current_ratio', 'quick_ratio', 'book_value_per_share'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For balance sheet ratios, higher is generally better
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Balance Sheet financials: Compare latest ended quarter to previous quarter
    if (['assets', 'equity', 'current_assets', 'current_liabilities', 'inventory', 'total_debt'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For balance sheet financials, higher is generally better (except liabilities and debt)
      if (statisticKey === 'current_liabilities' || statisticKey === 'total_debt') {
        if (latest < previous) return { direction: 'down', isGood: true };
        if (latest > previous) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    // Cash Flow section: Compare latest ended quarter to previous quarter
    if (['operating_cash_flow', 'investing_cash_flow', 'financing_cash_flow', 'end_cash_position', 'capital_expenditure', 'free_cash_flow', 'cash_flow_margin', 'ocf_ratio', 'fcf_yield'].includes(statisticKey)) {
      if (previous === undefined || previous === null) return { direction: null, isGood: false };
      
      // For cash flow metrics, higher is generally better (except capital expenditure and investing cash flow)
      if (statisticKey === 'capital_expenditure' || statisticKey === 'investing_cash_flow') {
        // For capital expenditure and investing cash flow, lower is better (less spending)
        if (latest < previous) return { direction: 'down', isGood: true };
        if (latest > previous) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (latest > previous) return { direction: 'up', isGood: true };
      if (latest < previous) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }
  }

  return { direction: null, isGood: false };
};

/**
 * Get arrow tooltip text based on comparison data
 */
export const getFundamentalsArrowTooltip = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseFundamentalsTabStatisticsOptions
): string => {
  const { timeframe } = options;
  const comparisonData = (ticker as any)._comparisonData;
  if (!comparisonData || !comparisonData[statisticKey]) {
    return '';
  }

  const data = comparisonData[statisticKey];
  const latest = data.latest;
  const previous = data.previous;
  const sameQuarterLastYear = data.sameQuarterLastYear;

  if (latest === undefined || latest === null) {
    return '';
  }

  // Format the comparison value based on the statistic type
  const formatComparisonValue = (value: number): string => {
    if (statisticKey === 'peg_ratio') {
      return value.toFixed(2);
    } else if (statisticKey === 'current_ratio' || statisticKey === 'quick_ratio') {
      return value.toFixed(2);
    } else if (statisticKey.includes('growth') || statisticKey.includes('margin') || 
               statisticKey === 'dividend_yield' || statisticKey === 'roa' || statisticKey === 'roe' || statisticKey === 'roic' ||
               statisticKey === 'cash_flow_margin') {
      return value.toFixed(2) + '%';
    } else if (statisticKey === 'ocf_ratio' || statisticKey === 'fcf_yield') {
      return value.toFixed(2);
    } else if (statisticKey.includes('pe_ratio') || statisticKey === 'ps_ratio' || 
               statisticKey === 'pb_ratio' || statisticKey === 'book_value_per_share' ||
               statisticKey === 'ev_fcf_ratio' || statisticKey === 'p_fcf_ratio') {
      return value.toFixed(2);
    } else {
      // For financial values, format with appropriate units
      if (Math.abs(value) >= 1e9) {
        return '$' + (value / 1e9).toFixed(2) + 'B';
      } else if (Math.abs(value) >= 1e6) {
        return '$' + (value / 1e6).toFixed(2) + 'M';
      } else if (Math.abs(value) >= 1e3) {
        return '$' + (value / 1e3).toFixed(2) + 'K';
      } else {
        return '$' + value.toFixed(2);
      }
    }
  };

  // Determine which comparison to use based on the section and timeframe
  if (timeframe === 'annual') {
    // Annual timeframe: All comparisons are latest year vs previous year
    if (previous !== undefined && previous !== null) {
      return `Compared to ${formatComparisonValue(previous)} the previous year`;
    }
  } else {
    // Quarterly timeframe: Original logic
    // Latest Valuation section: Compare current quarter to latest ended quarter
    if (['pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio', 'ev_fcf_ratio', 'p_fcf_ratio', 'dividend_yield'].includes(statisticKey)) {
      if (previous !== undefined && previous !== null) {
        return `Compared to ${formatComparisonValue(previous)} the previous quarter`;
      }
    }

    // Growth section: Compare latest ended quarter to same quarter last year
    if (['qoq_revenue_growth', 'qoq_net_income_growth', 'qoq_eps_growth', 'yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth'].includes(statisticKey)) {
      if (sameQuarterLastYear !== undefined && sameQuarterLastYear !== null) {
        return `Compared to ${formatComparisonValue(sameQuarterLastYear)} one year ago`;
      }
    }

    // Profitability section: Compare latest ended quarter to previous quarter
    if (['gross_margin', 'operating_margin', 'net_margin', 'roa', 'roe', 'roic', 'revenues', 'gross_profit', 'operating_expense', 'sg_and_a', 'r_and_d', 'operating_income_loss', 'net_income_loss', 'diluted_earnings_per_share_adjusted'].includes(statisticKey)) {
      if (previous !== undefined && previous !== null) {
        return `Compared to ${formatComparisonValue(previous)} the previous quarter`;
      }
    }

    // Balance Sheet section: Compare latest ended quarter to previous quarter
    if (['current_ratio', 'quick_ratio', 'book_value_per_share', 'assets', 'total_debt', 'equity', 'current_assets', 'current_liabilities', 'inventory'].includes(statisticKey)) {
      if (previous !== undefined && previous !== null) {
        return `Compared to ${formatComparisonValue(previous)} the previous quarter`;
      }
    }

    // Cash Flow section: Compare latest ended quarter to previous quarter
    if (['operating_cash_flow', 'investing_cash_flow', 'financing_cash_flow', 'end_cash_position', 'capital_expenditure', 'free_cash_flow', 'cash_flow_margin', 'ocf_ratio', 'fcf_yield'].includes(statisticKey)) {
      if (previous !== undefined && previous !== null) {
        return `Compared to ${formatComparisonValue(previous)} the previous quarter`;
      }
    }
  }

  return '';
};

/**
 * Format statistic value for Fundamentals tab
 */
export const formatFundamentalsStatisticValue = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseFundamentalsTabStatisticsOptions
): React.ReactNode => {
  const { timeframe, syncingTickers, unsyncedTickers, pollingLimitReached } = options;
  
  const symbol = ticker.ticker;
  
  // Handle syncing/loading states (for ListFundamentals)
  if (syncingTickers || unsyncedTickers || pollingLimitReached) {
    const isSyncing = syncingTickers?.has(symbol);
    const isUnsynced = unsyncedTickers?.has(symbol);
    const isPollingLimitReached = pollingLimitReached?.has(symbol);
    
    // Show - if polling limit reached for unsynced tickers
    if (isPollingLimitReached) {
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    // Show loading indicator for syncing, unsynced tickers, or when ticker is in syncing state (no data fetched yet)
    if (isSyncing || isUnsynced || ticker.isSyncing) {
      return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
    }
  }
  
  const value = ticker[statisticKey as keyof TickerDisplayData] as number;
  const statistics = timeframe === 'annual' ? ANNUAL_FUNDAMENTAL_STATISTICS : FUNDAMENTAL_STATISTICS;
  const statistic = statistics.find(s => s.key === statisticKey);
  
  if (value === undefined || value === null) {
    return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  }
  
  let formattedValue: string;
  
  if (statistic?.type === 'ratio') {
    // Define statistics that should ALWAYS show with % symbol
    const alwaysPercentageStats = [
      'dividend_yield',
      'qoq_revenue_growth', 'qoq_net_income_growth', 'qoq_eps_growth',
      'yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth',
      'gross_margin', 'operating_margin', 'net_margin',
      'roa', 'roe', 'roic', 'cash_flow_margin', 'fcf_yield'
    ];
    
    // Define statistics that should NEVER show with % symbol
    const neverPercentageStats = [
      'pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio', 'peg_ratio',
      'current_ratio', 'quick_ratio', 'book_value_per_share',
      'ocf_ratio'
    ];
    
    if (alwaysPercentageStats.includes(statisticKey)) {
      // Always show as percentage
      formattedValue = value.toFixed(2) + '%';
    } else if (neverPercentageStats.includes(statisticKey)) {
      // Always show as decimal without percentage
      formattedValue = value.toFixed(2);
    } else {
      // For other ratios, format as percentages or decimals based on magnitude
      if (value > 1) {
        formattedValue = value.toFixed(2);
      } else {
        formattedValue = (value * 100).toFixed(2) + '%';
      }
    }
  } else {
    // Format financial values with appropriate units
    if (Math.abs(value) >= 1e9) {
      formattedValue = '$' + (value / 1e9).toFixed(2) + 'B';
    } else if (Math.abs(value) >= 1e6) {
      formattedValue = '$' + (value / 1e6).toFixed(2) + 'M';
    } else if (Math.abs(value) >= 1e3) {
      formattedValue = '$' + (value / 1e3).toFixed(2) + 'K';
    } else {
      formattedValue = '$' + value.toFixed(2);
    }
  }

  // Get arrow direction and color
  const arrowInfo = getFundamentalsArrowInfo(statisticKey, ticker, options);
  const textColor = getFundamentalsStatisticColor(statisticKey, ticker, options);
  const tooltipText = getFundamentalsArrowTooltip(statisticKey, ticker, options);
  
  return (
    <span className={`inline-flex items-center gap-1 ${textColor}`}>
      {formattedValue}
      {arrowInfo.direction === 'up' && (
        <div className="relative group/arrow">
          <UpArrowIcon 
            className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
          />
          {tooltipText && (
            <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
              {tooltipText}
            </div>
          )}
        </div>
      )}
      {arrowInfo.direction === 'down' && (
        <div className="relative group/arrow">
          <DownArrowIcon 
            className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
          />
          {tooltipText && (
            <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
              {tooltipText}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

/**
 * Get statistic color for Fundamentals tab
 */
export const getFundamentalsStatisticColor = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseFundamentalsTabStatisticsOptions
): string => {
  // Use secondary text color for all fundamental values
  // Arrow colors provide the visual indication of good/bad changes
  return 'text-text-light-neutral dark:text-text-secondary';
};

